import { useState, useEffect, ReactElement } from "react";
import {
  TextField,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";

interface DeleteRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onDelete: () => Promise<void>;
  deleting: boolean;
  roleName: string;
  hasMembers: boolean;
}

function DeleteRoleDialog({
  open,
  onClose,
  onDelete,
  deleting,
  roleName,
  hasMembers,
}: DeleteRoleDialogProps): ReactElement {
  const [confirmName, setConfirmName] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setConfirmName("");
      setError("");
    }
  }, [open]);

  const handleDelete = async (): Promise<void> => {
    if (confirmName.trim() !== roleName) {
      setError("Role name does not match");
      return;
    }

    setError("");
    await onDelete();
    
    // Reset form on success
    if (!deleting) {
      setConfirmName("");
    }
  };

  const handleClose = (): void => {
    if (!deleting) {
      setConfirmName("");
      setError("");
      onClose();
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Delete Role"
      confirmText="Delete"
      onConfirm={handleDelete}
      confirmDisabled={deleting || confirmName.trim() !== roleName}
      disableClose={deleting}
      confirmColor="error"
      maxWidth="sm"
    >
      <Box>
        {hasMembers && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This role cannot be deleted because there are users assigned to it. Please reassign or remove those users first.
          </Alert>
        )}
        
        {!hasMembers && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone. This will permanently delete the role and remove it from all permissions.
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              To confirm deletion, please type the role name <strong>{roleName}</strong> below:
            </Typography>
            <TextField
              fullWidth
              label="Role Name"
              value={confirmName}
              onChange={(e) => {
                setConfirmName(e.target.value);
                setError("");
              }}
              error={!!error}
              helperText={error || "Type the role name to confirm deletion"}
              disabled={deleting}
              autoFocus
            />
          </>
        )}
      </Box>
    </StandardDialog>
  );
}

export default DeleteRoleDialog;
